import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';

@Component({
  selector: 'app-blind-upload-modal',
  templateUrl: './blind-upload-modal.component.html',
  styleUrls: ['./blind-upload-modal.component.scss']
})
export class BlindUploadModalComponent implements OnInit {
  uploadForm: FormGroup;
  poolPoints: any[] = [];
  clients: any[] = [];
  distributionCenters: any[] = [];

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<BlindUploadModalComponent>,
    private http: HttpClient,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.uploadForm = this.fb.group({
      pool_point_uuid: ['', Validators.required],
      pool_point_client_uuid: ['', Validators.required],
      customer_contact_distribution_center_uuid: ['', Validators.required],
      shipments: this.fb.array([])
    });
  }

  ngOnInit() {
    this.http.get<any[]>('/api/trip_lists/blind_upload_selects.php?type=pool_points')
      .subscribe(res => this.poolPoints = Array.isArray(res) ? res : Object.values(res));
  }

  get shipments(): FormArray {
    return this.uploadForm.get('shipments') as FormArray;
  }

  onPoolPointChange(pool_point_uuid: string) {
    this.clients = [];
    this.distributionCenters = [];
    this.uploadForm.patchValue({
      pool_point_client_uuid: '',
      customer_contact_distribution_center_uuid: ''
    });

    this.http.get<any[]>(`/api/trip_lists/blind_upload_selects.php?type=clients&pool_point_uuid=${pool_point_uuid}`)
      .subscribe(res => this.clients = Array.isArray(res) ? res : Object.values(res));
  }

  onClientChange(client_uuid: string) {
    const client = this.clients.find(c => c.value === client_uuid);
    if (!client) return;

    this.http.get<any[]>(`/api/trip_lists/blind_upload_selects.php?type=distribution_centers&customer_contact_uuid=${client.customer_contact_uuid}`)
      .subscribe(res => this.distributionCenters = Array.isArray(res) ? res : Object.values(res));
  }

  onFileSelected(event: any) {
    const file: File = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e: any) => {
      const text = e.target.result as string;
      this.parseCsvByHeader(text);
    };
    reader.readAsText(file);
  }

  /** Robust CSV parse using header names (supports commas or tabs, quoted cells, BOM). */
  private parseCsvByHeader(csv: string) {
    // Reset current rows
    while (this.shipments.length) this.shipments.removeAt(0);

    // Normalize line endings; remove BOM
    const rawLines = csv.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n').map(l => l.trim());
    const lines = rawLines.filter(l => l.length > 0);
    if (lines.length === 0) return;

    let headerLine = lines.shift()!;
    headerLine = headerLine.replace(/^\uFEFF/, '');

    // Detect delimiter
    const hasTab = headerLine.indexOf('\t') >= 0;
    const delimiter = hasTab ? '\t' : ',';

    const headers = this.splitRow(headerLine, delimiter).map(h => this.norm(h));

    // Build header index map with friendly aliases
    const idx = (names: string[]) => {
      for (const n of names) {
        const i = headers.indexOf(this.norm(n));
        if (i !== -1) return i;
      }
      return -1;
    };

    const col = {
      store_number: idx(['store_number', 'store #', 'store', 'store_no', 'store_num']),
      products: idx(['products', 'product_count', 'cartons']),
      weight: idx(['weight', 'reported_weight']),
      external_bill_of_lading: idx(['external_bill_of_lading', 'bol', 'bill_of_lading']),
      alternate_reference: idx(['alternate_reference', 'alt_ref', 'alt reference']),
      trailer_id: idx(['trailer_id', 'trailer']),
      asn_number: idx(['asn_number', 'asn'])
    };

    const requiredMissing = ['store_number','products','alternate_reference']
      .filter(k => (col as any)[k] === -1);
    if (requiredMissing.length) {
      console.error('Missing required CSV headers:', requiredMissing);
      return;
    }

    // Parse rows
    for (const line of lines) {
      const cells = this.splitRow(line, delimiter).map(v => this.unquote(v));

      const v = (i: number) => i >= 0 && i < cells.length ? cells[i] : '';

      this.shipments.push(this.fb.group({
        store_number: [v(col.store_number), Validators.required],
        products: [v(col.products), Validators.required],
        weight: [v(col.weight)],
        external_bill_of_lading: [v(col.external_bill_of_lading)],
        alternate_reference: [v(col.alternate_reference), Validators.required],
        trailer_id: [v(col.trailer_id)],
        asn_number: [v(col.asn_number)]
      }));
    }
  }

  /** Split a CSV row by delimiter honoring quotes. */
  private splitRow(row: string, delimiter: string): string[] {
    const out: string[] = [];
    let cur = '';
    let inQuotes = false;

    for (let i = 0; i < row.length; i++) {
      const ch = row[i];

      if (ch === '"') {
        if (inQuotes && row[i + 1] === '"') {
          cur += '"'; i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (ch === delimiter && !inQuotes) {
        out.push(cur);
        cur = '';
      } else {
        cur += ch;
      }
    }
    out.push(cur);
    return out;
  }

  /** Normalize header labels */
  private norm(s: string): string {
    return (s || '').toString().trim().toLowerCase().replace(/\s+/g, '_');
  }

  /** Remove wrapping quotes and trim */
  private unquote(s: string): string {
    const t = (s || '').trim();
    if (t.length >= 2 && t.startsWith('"') && t.endsWith('"')) {
      return t.slice(1, -1).replace(/""/g, '"').trim();
    }
    return t;
  }

  addShipment() {
    this.shipments.push(this.fb.group({
      store_number: ['', Validators.required],
      products: ['', Validators.required],
      weight: [''],
      external_bill_of_lading: [''],
      alternate_reference: ['', Validators.required],
      trailer_id: [''],
      asn_number: ['']
    }));
  }

  removeShipment(index: number) {
    this.shipments.removeAt(index);
  }

  allSelectsChosen(): boolean {
    return (this.uploadForm.get('pool_point_uuid')?.valid ?? false) &&
           (this.uploadForm.get('pool_point_client_uuid')?.valid ?? false) &&
           (this.uploadForm.get('customer_contact_distribution_center_uuid')?.valid ?? false);
  }

  submit() {
  if (this.uploadForm.valid) {
    const payload = {
      trip: this.data.trip,
      ...this.uploadForm.value
    };

    this.http.post('/api/trip_lists/blind_shipment_data.php', payload)
      .subscribe({
        next: (res) => {
          console.log('Blind shipment data saved:', res);
          this.dialogRef.close(res); // close modal, pass response back
        },
        error: (err) => {
          console.error('Error saving blind shipment data', err);
        }
      });
  }
}


  cancel() {
    this.dialogRef.close();
  }
}
